/* eslint-disable max-lines */
import { ViteEnv } from '@/env';
import type { ScanProgress } from '@/hooks/use-scan-progress';
import type { GcpServiceAccountForList } from '@/lib/queries/gcpServiceAccount';
import type { AwsCredentialsForList } from '@/lib/queries/integrations/cloud/aws/credentials';
import type { AwsRole } from '@/lib/queries/integrations/cloud/aws/roles';
import type { K8SClusterStatus } from '@/lib/queries/integrations/containers/k8sClusters';
import type { PagerDutyTokenForList } from '@/lib/queries/integrations/incidentsManagement/pagerduty/tokens';
import type { DatadogApiCredentialsForList } from '@/lib/queries/integrations/monitoring/datadog';
import type { GrafanaConfigurationForList } from '@/lib/queries/integrations/monitoring/grafana';
import type { GithubConfig } from '@/lib/queries/integrations/scm/github';
import type { GitlabConfig } from '@/lib/queries/integrations/scm/gitlab';
import type { HCPCloudTokenForList } from '@/lib/queries/integrations/terraformBackend/hcpCloud';

import type { IntegrationsData } from './-components/Integrations';
import awsLogo from './-components/logos/aws.svg?url';
import datadogLogo from './-components/logos/datadog.svg?url';
import gcpLogo from './-components/logos/gcp.svg?url';
import grafanaLogo from './-components/logos/grafana.svg?url';
import githubLogo from './-components/logos/github.svg?url';
import gitlabLogo from './-components/logos/gitlab.svg?url';
import hashicorpLogo from './-components/logos/hashicorp.svg?url';
import kubernetesLogo from './-components/logos/kubernetes.svg?url';
import pagerdutyLogo from './-components/logos/pagerduty.svg?url';
import slackLogo from './-components/logos/slack.svg?url';

const getGithubIntegration = ({
  githubInstall,
}: {
  githubInstall: GithubConfig[] | undefined;
}): IntegrationsData[number]['items'][number] => ({
  title: 'integrations.categories.scm.github',
  description: 'integrations.descriptions.scm.github',
  logoUrl: githubLogo,
  connected: (githubInstall ?? []).length > 0,
  available: true,
  onClick: () => {
    window.location.href = `https://github.com/login/oauth/authorize?client_id=${ViteEnv.VITE_GITHUB_CLIENT_ID}&scope=read:user,user:email`;
  },
  pending: githubInstall === undefined,
});

const getGitlabIntegration = ({
  gitlabInstall,
  gitlabEnabled,
}: {
  gitlabInstall: GitlabConfig[] | undefined;
  gitlabEnabled: boolean;
}): IntegrationsData[number]['items'][number] => {
  const isConnected = (gitlabInstall ?? []).length > 0;

  if (!gitlabEnabled) {
    return {
      title: 'integrations.categories.scm.gitlab',
      description: 'integrations.descriptions.scm.gitlab',
      logoUrl: gitlabLogo,
      connected: false,
      available: false,
    };
  }

  return {
    title: 'integrations.categories.scm.gitlab',
    description: 'integrations.descriptions.scm.gitlab',
    logoUrl: gitlabLogo,
    connected: isConnected,
    available: true,
    ...(isConnected
      ? { to: '/integrations/gitlab' } // Navigate to GitLab config page when connected
      : {
          onClick: () => {
            const redirectUri = encodeURIComponent(
              ViteEnv.VITE_GITLAB_REDIRECT_URI,
            );
            window.location.href = `https://gitlab.com/oauth/authorize?client_id=${ViteEnv.VITE_GITLAB_CLIENT_ID}&redirect_uri=${redirectUri}&response_type=code`;
          },
        }), // Use OAuth flow when not connected
    pending: gitlabInstall === undefined,
  };
};

const getAwsIntegration = ({
  awsRoles,
  awsUsers,
  scanProgress,
}: {
  awsRoles: AwsRole[] | undefined;
  awsUsers: AwsCredentialsForList[] | undefined;
  scanProgress?: ScanProgress;
}): IntegrationsData[number]['items'][number] => {
  const allCreds = [...(awsUsers ?? []), ...(awsRoles ?? [])];
  const isSyncing = allCreds.some(({ status }) => status === 'inactive');
  const awsUsersError = (awsUsers ?? []).some(
    ({ status }) => status === 'error',
  );
  const awsRolesError = (awsRoles ?? []).some(
    ({ status }) => status === 'error',
  );

  const donePercentage = Math.floor(
    (100 * allCreds.filter(({ status }) => status === 'active').length) /
      Math.max(allCreds.length, 1),
  );

  return {
    title: 'integrations.categories.publicCloud.aws',
    description: 'integrations.descriptions.publicCloud.aws',
    logoUrl: awsLogo,
    connected: allCreds.length > 0,
    available: true,
    to: '/integrations/aws',
    pending: awsUsers === undefined || awsRoles === undefined,
    warnings: [
      ...(awsUsersError ? ['integrations.aws.warningUser' as const] : []),
      ...(awsRolesError ? ['integrations.aws.warningRole' as const] : []),
    ],
    donePercentage,
    scanProgress,
    isSyncing,
  };
};

const getGcpDisabledIntegration =
  (): IntegrationsData[number]['items'][number] => ({
    title: 'integrations.categories.publicCloud.gcp',
    description: 'integrations.descriptions.publicCloud.gcp',
    logoUrl: gcpLogo,
    connected: false,
    available: false,
  });

const getGcpIntegration = ({
  gcpServiceAccounts,
  gcpEnabled,
  scanProgress,
}: {
  gcpServiceAccounts: GcpServiceAccountForList[] | undefined;
  gcpEnabled: boolean;
  scanProgress?: ScanProgress;
}): IntegrationsData[number]['items'][number] => {
  if (!gcpEnabled) {
    return getGcpDisabledIntegration();
  }

  const gcpError = (gcpServiceAccounts ?? []).some(
    ({ status }) => status === 'error',
  );
  const isSyncing = (gcpServiceAccounts ?? []).some(
    ({ status }) => status === 'inactive',
  );

  const donePercentage = Math.floor(
    (100 *
      (gcpServiceAccounts ?? []).filter(({ status }) => status === 'active')
        .length) /
      Math.max((gcpServiceAccounts ?? []).length, 1),
  );

  return {
    title: 'integrations.categories.publicCloud.gcp',
    description: 'integrations.descriptions.publicCloud.gcp',
    logoUrl: gcpLogo,
    connected: (gcpServiceAccounts ?? []).length > 0,
    available: true,
    to: '/integrations/gcp/service-accounts',
    pending: gcpServiceAccounts === undefined,
    warnings: gcpError
      ? ['integrations.gcp.warningServiceAccount' as const]
      : [],
    donePercentage,
    scanProgress,
    isSyncing,
  };
};

// Temporarily hidden S3 terraform backend - easy to reactivate
// const getTerraformBackendIntegration = ({
//   buckets,
// }: {
//   buckets: S3Bucket[] | undefined;
// }): IntegrationsData[number]['items'][number] => {
//   const okBuckets = (buckets ?? [])
//     .filter(({ syncStatus }) => syncStatus === 'synced')
//     .filter(({ accessStatus }) => accessStatus === 'active');
//
//   const donePercentage = Math.floor(
//     (100 * okBuckets.length) /
//       Math.max(
//         (buckets ?? []).filter(
//           ({ syncStatus }) => syncStatus !== 'unsynced-with-code',
//         ).length,
//         1,
//       ),
//   );
//
//   return {
//     title: 'integrations.categories.terraformBackend.s3',
//     logoUrl: s3Logo,
//     connected: (buckets ?? []).length > 0,
//     available: true,
//     pending: buckets === undefined,
//     to: '/integrations/terraform-backend/buckets',
//     warnings: [
//       ...((buckets ?? []).some(
//         ({ syncStatus, accessStatus }) =>
//           syncStatus === 'unsynced-with-cloud' && accessStatus === 'no-creds',
//       )
//         ? ['integrations.terraformBackend.buckets.warningUnsynced' as const]
//         : []),
//       ...((buckets ?? []).some(({ accessStatus }) => accessStatus === 'error')
//         ? ['integrations.terraformBackend.buckets.warningAccess' as const]
//         : []),
//     ],
//     donePercentage,
//   };
// };

const getHCPIntegration = ({
  hcpCloudTokens,
}: {
  hcpCloudTokens: HCPCloudTokenForList[] | undefined;
}): IntegrationsData[number]['items'][number] => {
  const donePercentage = Math.floor(
    (100 *
      (hcpCloudTokens ?? []).filter(({ status }) => status === 'active')
        .length) /
      Math.max((hcpCloudTokens ?? []).length, 1),
  );

  return {
    title: 'integrations.categories.terraformBackend.hcp',
    description: 'integrations.descriptions.terraformBackend.hcp',
    logoUrl: hashicorpLogo,
    connected: (hcpCloudTokens ?? []).length > 0,
    available: true,
    donePercentage,
    pending: hcpCloudTokens === undefined,
    to: '/integrations/terraform-backend/hcp-cloud',
    warnings: [
      ...(hcpCloudTokens?.some(({ status }) => status === 'error') === true
        ? ['integrations.terraformBackend.hcpCloud.warningError' as const]
        : []),
    ],
  };
};

const getDatadogIntegration = ({
  datadogApiCredentials,
  datadogEnabled,
}: {
  datadogApiCredentials: DatadogApiCredentialsForList[] | undefined;
  datadogEnabled: boolean;
}): IntegrationsData[number]['items'][number] => {
  if (datadogEnabled) {
    return {
      title: 'integrations.categories.monitoring.datadog',
      description: 'integrations.descriptions.monitoring.datadog',
      logoUrl: datadogLogo,
      connected:
        (datadogApiCredentials ?? []).filter(
          ({ status }) => status === 'active',
        ).length > 0,
      available: true,
      pending: datadogApiCredentials === undefined,
      to: '/integrations/monitoring/datadog-api-credentials',
      warnings: [
        ...(datadogApiCredentials?.some(({ status }) => status === 'error') ===
        true
          ? ['integrations.monitoring.datadog.warningError' as const]
          : []),
      ],
      donePercentage: Math.floor(
        (100 *
          (datadogApiCredentials ?? []).filter(
            ({ status }) => status === 'active',
          ).length) /
          Math.max((datadogApiCredentials ?? []).length, 1),
      ),
    };
  }

  return {
    title: 'integrations.categories.monitoring.datadog',
    description: 'integrations.descriptions.monitoring.datadog',
    logoUrl: datadogLogo,
    connected: false,
    available: false,
  };
};

const getGrafanaIntegration = ({
  grafanaConfigurations,
  grafanaEnabled,
}: {
  grafanaConfigurations: GrafanaConfigurationForList[] | undefined;
  grafanaEnabled: boolean;
}): IntegrationsData[number]['items'][number] => {
  if (grafanaEnabled) {
    return {
      title: 'integrations.categories.monitoring.grafana',
      description: 'integrations.descriptions.monitoring.grafana',
      logoUrl: grafanaLogo,
      connected:
        (grafanaConfigurations ?? []).filter(
          ({ status }) => status === 'active',
        ).length > 0,
      available: true,
      pending: grafanaConfigurations === undefined,
      to: '/integrations/monitoring/grafana',
      warnings: [
        ...(grafanaConfigurations?.some(({ status }) => status === 'error') ===
        true
          ? ['integrations.monitoring.grafana.warningError' as const]
          : []),
      ],
      donePercentage: Math.floor(
        (100 *
          (grafanaConfigurations ?? []).filter(
            ({ status }) => status === 'active',
          ).length) /
          Math.max((grafanaConfigurations ?? []).length, 1),
      ),
    };
  }

  return {
    title: 'integrations.categories.monitoring.grafana',
    description: 'integrations.descriptions.monitoring.grafana',
    logoUrl: grafanaLogo,
    connected: false,
    available: false,
  };
};

const getKubernetesIntegration = ({
  k8sClusters,
}: {
  k8sClusters: K8SClusterStatus[] | undefined;
}): IntegrationsData[number]['items'][number] => {
  if (k8sClusters === undefined) {
    return {
      title: 'integrations.categories.containers.kubernetes',
      description: 'integrations.descriptions.containers.kubernetes',
      logoUrl: kubernetesLogo,
      connected: false,
      available: true,
      pending: true,
    };
  }

  if (k8sClusters.length === 0) {
    return {
      title: 'integrations.categories.containers.kubernetes',
      description: 'integrations.descriptions.containers.kubernetes',
      logoUrl: kubernetesLogo,
      connected: false,
      available: true,
      to: '/integrations/containers/kubernetes',
    };
  }

  const now = new Date();
  const twentyMinutesAgo = new Date(now.getTime() - 20 * 60 * 1000);

  const healthyClusters = k8sClusters.filter(
    cluster => new Date(cluster.last_heartbeat) > twentyMinutesAgo,
  );

  const connected = k8sClusters.length > 0;
  const hasStale =
    healthyClusters.length < k8sClusters.length && k8sClusters.length > 0;

  const donePercentage = Math.floor(
    (100 * healthyClusters.length) / k8sClusters.length,
  );

  return {
    title: 'integrations.categories.containers.kubernetes',
    description: 'integrations.descriptions.containers.kubernetes',
    logoUrl: kubernetesLogo,
    connected,
    available: true,
    to: '/integrations/containers/kubernetes',
    pending: false,
    warnings: hasStale
      ? ['integrations.containers.kubernetes.warningStale' as const]
      : [],
    donePercentage,
  };
};

const getPagerDutyIntegration = ({
  pagerDutyTokens,
  pagerdutyEnabled,
}: {
  pagerDutyTokens: PagerDutyTokenForList[] | undefined;
  pagerdutyEnabled: boolean;
}): IntegrationsData[number]['items'][number] => {
  if (pagerdutyEnabled) {
    return {
      title: 'integrations.categories.incidentsManagement.pagerduty',
      description: 'integrations.descriptions.incidentsManagement.pagerduty',
      logoUrl: pagerdutyLogo,
      connected: (pagerDutyTokens ?? []).length > 0,
      available: true,
      pending: pagerDutyTokens === undefined,
      to: '/integrations/incidents-management/pagerduty',
    };
  }

  return {
    title: 'integrations.categories.incidentsManagement.pagerduty',
    description: 'integrations.descriptions.incidentsManagement.pagerduty',
    logoUrl: pagerdutyLogo,
    connected: false,
    available: false,
  };
};

const getSlackIntegration = ({
  slackConnected,
}: {
  slackConnected: boolean;
}): IntegrationsData[number]['items'][number] => ({
  title: 'integrations.categories.messaging.slack',
  description: 'integrations.descriptions.messaging.slack',
  logoUrl: slackLogo,
  connected: slackConnected,
  available: true,
  to: '/integrations/messaging/slack',
});

export const getIntegrationData = ({
  awsRoles,
  awsUsers,
  gcpServiceAccounts,
  githubInstall,
  gitlabInstall,
  gitlabEnabled,
  // buckets,
  hcpCloudTokens,
  datadogApiCredentials,
  datadogEnabled,
  grafanaConfigurations,
  grafanaEnabled,
  gcpEnabled,
  k8sClusters,
  awsScanProgress,
  gcpScanProgress,
  pagerDutyTokens,
  pagerdutyEnabled,
  slackConnected,
}: {
  awsRoles: AwsRole[] | undefined;
  awsUsers: AwsCredentialsForList[] | undefined;
  gcpServiceAccounts: GcpServiceAccountForList[] | undefined;
  githubInstall: GithubConfig[] | undefined;
  gitlabInstall: GitlabConfig[] | undefined;
  gitlabEnabled: boolean;
  // buckets: S3Bucket[] | undefined;
  hcpCloudTokens: HCPCloudTokenForList[] | undefined;
  datadogApiCredentials: DatadogApiCredentialsForList[] | undefined;
  datadogEnabled: boolean;
  grafanaConfigurations: GrafanaConfigurationForList[] | undefined;
  grafanaEnabled: boolean;
  gcpEnabled: boolean;
  k8sClusters: K8SClusterStatus[] | undefined;
  awsScanProgress?: ScanProgress;
  gcpScanProgress?: ScanProgress;
  pagerDutyTokens: PagerDutyTokenForList[] | undefined;
  pagerdutyEnabled: boolean;
  slackConnected: boolean;
}): IntegrationsData => [
  {
    categoryTitle: 'integrations.categories.scm.title',
    items: [
      getGithubIntegration({
        githubInstall,
      }),
      getGitlabIntegration({
        gitlabInstall,
        gitlabEnabled,
      }),
    ],
  },
  {
    categoryTitle: 'integrations.categories.publicCloud.title',
    items: [
      getAwsIntegration({
        awsRoles,
        awsUsers,
        scanProgress: awsScanProgress,
      }),
      getGcpIntegration({
        gcpServiceAccounts,
        gcpEnabled,
        scanProgress: gcpScanProgress,
      }),
    ],
  },
  {
    categoryTitle: 'integrations.categories.terraformBackend.title',
    items: [
      // getTerraformBackendIntegration({ buckets }), // Temporarily hidden - easy to reactivate
      getHCPIntegration({ hcpCloudTokens }),
    ],
  },
  {
    categoryTitle: 'integrations.categories.monitoring.title',
    items: [
      getDatadogIntegration({ datadogApiCredentials, datadogEnabled }),
      getGrafanaIntegration({ grafanaConfigurations, grafanaEnabled }),
    ],
  },
  {
    categoryTitle: 'integrations.categories.containers.title',
    items: [getKubernetesIntegration({ k8sClusters })],
  },
  {
    categoryTitle: 'integrations.categories.incidentsManagement.title',
    items: [getPagerDutyIntegration({ pagerDutyTokens, pagerdutyEnabled })],
  },
  {
    categoryTitle: 'integrations.categories.messaging.title',
    items: [getSlackIntegration({ slackConnected })],
  },
];
